import React, { useEffect, useMemo, useState } from "react";
import "./TicketingSystemInstanceConnectivity.scss";
import { createColumnHelper } from "@tanstack/react-table";
import { useLazyQuery } from "@apollo/client";
import { GET_TICKET_PROVIDER_CONNECTION } from "../../../TicketingSystemsApi";
import {
  TextButton,
  Tooltip,
  Table,
  TableCell,
  tableCellTypesEnum,
} from "@SeemplicityDev/shared";

interface ConnectivityTestResult {
  permissions: string;
  capabilities: string;
  entity: string;
  result: boolean;
  error: string;
}

interface TicketProviderConnectionData {
  ticket_provider_integration_validation: {
    tests_results: ConnectivityTestResult[];
  } | null;
}

interface TicketProviderConnectionVariables {
  ticket_provider_id: number;
}

interface TicketingSystemInstanceConnectivityProps {
  id?: number;
  runTestsOnLoad?: boolean;
}

const TicketingSystemInstanceConnectivity: React.FC<
  TicketingSystemInstanceConnectivityProps
> = ({ id, runTestsOnLoad }) => {
  const [testResults, setTestResults] = useState<
    ConnectivityTestResult[] | null
  >(null);
  const [getTicketingSystemInstanceTestConnection, { loading }] = useLazyQuery<
    TicketProviderConnectionData,
    TicketProviderConnectionVariables
  >(GET_TICKET_PROVIDER_CONNECTION, {
    fetchPolicy: "network-only",
    onCompleted: (data) => {
      setTestResults(
        data?.ticket_provider_integration_validation?.tests_results ?? null
      );
    },
  });

  useEffect(() => {
    if (runTestsOnLoad && id) {
      getTicketingSystemInstanceTestConnection({
        variables: { ticket_provider_id: id },
      });
    }
  }, [runTestsOnLoad, id]);

  const columnHelper = createColumnHelper<ConnectivityTestResult>();
  const columns = useMemo(
    () => [
      columnHelper.accessor("entity", {
        cell: (info) => (
          <TableCell type={tableCellTypesEnum.COMPONENT}>
            <Tooltip content={info.getValue()}>
              <div className={"text-cell"}>{info.getValue()}</div>
            </Tooltip>
          </TableCell>
        ),
        header: "Table Name",
        enableSorting: false,
        enableColumnFilter: false,
        size: 310,
      }),
      columnHelper.accessor("capabilities", {
        cell: (info) => (
          <TableCell type={tableCellTypesEnum.COMPONENT}>
            <Tooltip content={info.getValue()}>
              <div className={"text-cell"}>{info.getValue()}</div>
            </Tooltip>
          </TableCell>
        ),
        header: "Capability",
        enableSorting: false,
        enableColumnFilter: false,
        size: 420,
      }),
      columnHelper.accessor("result", {
        cell: (info) => {
          const error = info?.row?.original?.error;
          return (
            <TableCell
              type={tableCellTypesEnum.COMPONENT}
              className={"result-cell"}
            >
              {!!info?.getValue() ? (
                <>
                  <Tooltip type="info" content={"Validation Succeeded"}>
                    <i className={"seem-icon seem-icon-jira-icon-done"} />
                  </Tooltip>
                </>
              ) : (
                <>
                  <Tooltip type="info" content={error}>
                    <i className={"seem-icon seem-icon-circle-close"} />
                  </Tooltip>
                </>
              )}
            </TableCell>
          );
        },
        header: "Result",
        enableSorting: false,
        enableColumnFilter: false,
      }),
    ],
    [columnHelper]
  );

  return (
    <div className={"test-connection-connectivity"}>
      <div className={"test-connection-header"}>
        <div className={"header-title"}>Connectivity Test</div>
        <div className={"test-connection-description"}>
          For initial connectivity, we check the following tables for reading
          permissions
        </div>
        {!runTestsOnLoad && (
          <TextButton
            buttonStyle={"primary"}
            label={"Run Test"}
            type={"button"}
            rightIcon={loading ? "seem-icon-spinner spinner" : ""}
            disabled={loading || !!testResults?.length}
            data-testid={"test-connection"}
            onClick={() => {
              if (!!id) {
                getTicketingSystemInstanceTestConnection({
                  variables: { ticket_provider_id: id },
                });
              }
            }}
            title={"Run Test"}
          />
        )}
      </div>
      {(loading || testResults) && (
        <Table
          columns={columns}
          data={testResults}
          loading={loading}
          loaderRowsAmount={3}
        />
      )}
    </div>
  );
};

export default TicketingSystemInstanceConnectivity;
